import {
    createMfhRenovationProject,
    createMfhRenovationProjectCostBreakdown,
    createMfhRenovationProjectLot,
    createMfhRenovationProjectPlanningModule,
    createMfhRenovationProjectPosition,
    createMfhRenovationProjectPositions,
    deleteMfhRenovationProject,
    deleteMfhRenovationProjectLot,
    deleteMfhRenovationProjectPlanningModule,
    deleteMfhRenovationProjectPosition,
    fetchMfhRenovationProject,
    fetchMfhRenovationProjectCostBreakdown,
    fetchMfhRenovationProjectPlanningModule,
    fetchMfhRenovationProjects,
    postProcessMFHRenovationProjectCostBreakdownEvent,
    updateMfhRenovationProject,
    updateMfhRenovationProjectCostBreakdown,
    updateMfhRenovationProjectLot,
    updateMfhRenovationProjectPlanningModule,
    updateMfhRenovationProjectPosition,
    restoreMfhRenovationProject,
    restoreMfhRenovationProjectPlanningModule,
    createTenderingProcess,
    fetchTenderingProcess,
    updateTenderingProcess,
    deleteTenderingProcess,
    fetchMfhRenovationProjectLot,
    postMfhRenovationProjectLotEvent,
    postMfhRenovationProjectEvent,
    postMfhRenovationProjectPlanningModuleEvent,
    importTenderingProcessOffersFromExcel,
    updateTenderingProcessOffer,
    type IImportTenderingProcessOffersResult,
    fetchTenderingProcessTenderer,
    deleteTenderingProcessOffer,
    updateTenderingProcessWinnerTenderer,
    postTenderingProcessTendererEvent,
    fetchContracts,
    createContract,
    updateContract,
    uploadContractPdf,
    deleteContract,
    fetchContract,
    fetchCostBreakdownPositions,
} from '@/api/mfh';
import type {
    TenderingProcessExpandOpts,
    TenderingProcessTendererExpandOpts,
    CreateTenderingProcessRequest,
    ITenderingProcessTenderer,
    ICreateMFHRenovationProjectCostBreakdownParams,
    ICreateMFHRenovationProjectLotParams,
    ICreateMFHRenovationProjectParams,
    ICreateMFHRenovationProjectPlanningModuleParams,
    ICreateMFHRenovationProjectPositions,
    IMFHRenovationProjectPosition,
    IUpdateMFHRenovationProject,
    IUpdateMFHRenovationProjectCostBreakdownParams,
    IUpdateMFHRenovationProjectLotParams,
    IUpdateMFHRenovationProjectPlanningModule,
    IUpdateMFHRenovationProjectPositionParams,
    MFHRenovationProjectCostBreakdownEvent,
    MFHRenovationProjectCostBreakdownExpandOpts,
    MFHRenovationProjectEvents,
    MFHRenovationProjectExpandOpts,
    MFHRenovationProjectLotEvents,
    MFHRenovationProjectLotExpandOpts,
    MFHRenovationProjectPlanningModuleEvents,
    MFHRenovationProjectPlanningModuleExpandOpts,
    ReviewProcessEvents,
    UpdateTenderingProcessRequest,
    TenderingProcessTendererEvents,
    IContract,
    ContractExpandOpts,
    CreateContractRequest,
    UpdateContractRequest,
} from '@condo/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { MaybeRef } from 'vue';
import { computed, defineComponent, h, unref } from 'vue';
import { omit } from 'lodash-es';
import { showModal } from '@/shared';
import { NButton } from 'naive-ui';
import ExcelFileUpload from '@/components/mfh/one-mfh/renovation-project/tendering-process/ExcelFileUpload.vue';

export interface ITriggerMfhRenovationProjectParams {
    mfhRenovationProjectId: number;
    event: MFHRenovationProjectEvents;
    eventDate: Date;
    additionalData?: Record<string, any>;
}

export interface ITriggerMfhRenovationProjectPlanningModuleParams {
    mfhRenovationProjectPlanningModuleId: number;
    event: MFHRenovationProjectPlanningModuleEvents;
    eventDate: Date;
    additionalData?: Record<string, any>;
}

interface ITriggerMfhRenovationProjectCostBreakdownParams {
    mfhRenovationProjectCostBreakdownId: number;
    event: ReviewProcessEvents | MFHRenovationProjectCostBreakdownEvent;
    eventDate: Date;
    additionalData?: Record<string, any>;
}

export interface ITriggerMfhRenovationProjectLotParams {
    mfhRenovationProjectLotId: number;
    event: MFHRenovationProjectLotEvents;
    eventDate: Date;
    additionalData?: Record<string, any>;
}

export const useMfhRenovationProjects = (
    where: MaybeRef<{ mfhId: number; includeDeletedProjectsAndPlanningModules?: boolean }>,
    expandOpts: MFHRenovationProjectExpandOpts,
) => {
    return useQuery(['mfh-renovation-projects', 'mfhId', where, expandOpts], async () => fetchMfhRenovationProjects(unref(where), expandOpts), {
        initialData: [],
    });
};

export const useMfhRenovationProject = (mfhRenovationProjectId: MaybeRef<number | undefined>, expandOpts: MFHRenovationProjectExpandOpts) => {
    const enabled = computed(() => !!unref(mfhRenovationProjectId));
    return useQuery(
        ['mfh-renovation-projects', 'mfhRenovationProjectId', mfhRenovationProjectId, expandOpts],
        async () => fetchMfhRenovationProject(unref(mfhRenovationProjectId), expandOpts),
        { enabled },
    );
};

export const useTenderingProcess = (tenderingProcessId: MaybeRef<number | undefined>, expandOpts: TenderingProcessExpandOpts) => {
    const enabled = computed(() => !!unref(tenderingProcessId));
    return useQuery(
        ['mfh-renovation-projects', 'tenderingProcessId', tenderingProcessId, expandOpts],
        async () => fetchTenderingProcess(unref(tenderingProcessId), expandOpts),
        { enabled },
    );
};

export const useMfhRenovationProjectPlanningModule = (
    mfhRenovationProjectPlanningModuleId: MaybeRef<number | undefined>,
    expandOpts: MFHRenovationProjectPlanningModuleExpandOpts,
) => {
    const enabled = computed(() => !!unref(mfhRenovationProjectPlanningModuleId));

    return useQuery(
        ['mfh-renovation-projects', 'mfhRenovationProjectPlanningModuleId', mfhRenovationProjectPlanningModuleId, expandOpts],
        async () => fetchMfhRenovationProjectPlanningModule(unref(mfhRenovationProjectPlanningModuleId), expandOpts),
        { enabled },
    );
};

export const useContracts = (mfhRenovationProjectId: MaybeRef<number | undefined>, expandOpts: ContractExpandOpts) => {
    const enabled = computed(() => !!unref(mfhRenovationProjectId));
    return useQuery(
        ['contracts', 'mfhRenovationProjectId', mfhRenovationProjectId, expandOpts],
        async () => fetchContracts(unref(mfhRenovationProjectId)!, expandOpts),
        { enabled, initialData: [] },
    );
};

export const useContract = (contractId: MaybeRef<number | undefined>, expandOpts: ContractExpandOpts) => {
    const enabled = computed(() => !!unref(contractId));
    return useQuery(['contracts', 'contractId', contractId, expandOpts], async () => fetchContract(unref(contractId)!, expandOpts), {
        enabled,
        initialData: null,
    });
};

export const useContractMutations = () => {
    const queryClient = useQueryClient();

    const invalidate = () => {
        queryClient.invalidateQueries(['contracts']);
        queryClient.invalidateQueries(['mfh-renovation-projects']);
        queryClient.invalidateQueries(['positionCostAllocations']);
    };

    const createContractMutation = useMutation({
        mutationFn: async ({ mfhRenovationProjectId, contract }: { mfhRenovationProjectId: number; contract: CreateContractRequest['contract'] }) =>
            createContract(mfhRenovationProjectId, contract),
        onSuccess: invalidate,
    });

    const updateContractMutation = useMutation({
        mutationFn: async ({ contractId, contract }: { contractId: number; contract: UpdateContractRequest['contract'] }) =>
            updateContract(contractId, contract),
        onSuccess: invalidate,
    });

    const uploadContractPdfMutation = useMutation({
        mutationFn: async ({ contractId, files }: { contractId: number; files: File[] }) => uploadContractPdf(contractId, files),
        onSuccess: invalidate,
    });

    const deleteContractMutation = useMutation({
        mutationFn: async ({ contractId }: { contractId: number }) => deleteContract(contractId),
        onSuccess: invalidate,
    });

    return {
        createContract: createContractMutation.mutateAsync,
        updateContract: updateContractMutation.mutateAsync,
        uploadContractPdf: uploadContractPdfMutation.mutateAsync,
        deleteContract: deleteContractMutation.mutateAsync,
    };
};

export const useTenderingProcessTenderer = (tenderingProcessTendererId: MaybeRef<number | undefined>, expandOpts: TenderingProcessTendererExpandOpts) => {
    const enabled = computed(() => !!unref(tenderingProcessTendererId));
    return useQuery(
        ['mfh-renovation-projects', 'tenderingProcessTendererId', tenderingProcessTendererId, expandOpts],
        async () => fetchTenderingProcessTenderer(unref(tenderingProcessTendererId), expandOpts),
        { enabled },
    );
};

export const useMfhRenovationProjectCostBreakdown = (
    mfhRenovationProjectCostBreakdownId: MaybeRef<number | undefined>,
    expandOpts: MFHRenovationProjectCostBreakdownExpandOpts,
) => {
    const enabled = computed(() => !!unref(mfhRenovationProjectCostBreakdownId));
    return useQuery(
        ['mfh-renovation-projects', 'mfhRenovationProjectCostBreakdownId', mfhRenovationProjectCostBreakdownId, expandOpts],
        async () => fetchMfhRenovationProjectCostBreakdown(unref(mfhRenovationProjectCostBreakdownId)!, expandOpts),
        { enabled },
    );
};

export const useMfhRenovationProjectLot = (mfhRenovationProjectLotId: MaybeRef<number | undefined>, expandOpts: MFHRenovationProjectLotExpandOpts) => {
    const enabled = computed(() => !!unref(mfhRenovationProjectLotId));
    return useQuery(
        ['mfh-renovation-projects', 'mfhRenovationProjectLotId', mfhRenovationProjectLotId, expandOpts],
        async () => fetchMfhRenovationProjectLot(unref(mfhRenovationProjectLotId)!, expandOpts),
        { enabled },
    );
};

export const useCostBreakdownPositions = (costBreakdownId: MaybeRef<number | undefined>) => {
    return useQuery({
        queryKey: ['cost-breakdown-positions', costBreakdownId],
        queryFn: () => fetchCostBreakdownPositions(unref(costBreakdownId)!),
        enabled: !!costBreakdownId,
    });
};

export const useMfhRenovationProjectMutations = () => {
    const queryClient = useQueryClient();
    const createMfhRenovationProjectMutation = useMutation({
        mutationFn: async (params: ICreateMFHRenovationProjectParams) => {
            await createMfhRenovationProject(params);
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
            queryClient.invalidateQueries(['mfh-buildings']);
        },
    });

    const deleteMfhRenovationProjectMutation = useMutation({
        mutationFn: async (mfhRenovationProjectId: number) => {
            await deleteMfhRenovationProject(mfhRenovationProjectId);
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
            queryClient.invalidateQueries(['mfh-buildings']);
        },
    });

    const restoreMfhRenovationProjectMutation = useMutation({
        mutationFn: async (mfhRenovationProjectId: number) => {
            await restoreMfhRenovationProject(mfhRenovationProjectId);
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
            queryClient.invalidateQueries(['mfh-buildings']);
        },
    });

    const updateMfhRenovationProjectMutation = useMutation({
        mutationFn: async (params: { mfhRenovationProjectId: number; data: IUpdateMFHRenovationProject['data'] }) => {
            await updateMfhRenovationProject(params.mfhRenovationProjectId, params.data);
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
            queryClient.invalidateQueries(['mfh-buildings']);
        },
    });

    const createMFHRenovationProjectPlanningModuleMutation = useMutation({
        mutationFn: async (params: { data: ICreateMFHRenovationProjectPlanningModuleParams }) => {
            await createMfhRenovationProjectPlanningModule(params.data.mfhRenovationProjectId, omit(params.data, 'mfhRenovationProjectId'));
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const updateMfhRenovationProjectPlanningModuleMutation = useMutation({
        mutationFn: async (params: { mfhRenovationProjectPlanningModuleId: number; data: IUpdateMFHRenovationProjectPlanningModule['data'] }) => {
            await updateMfhRenovationProjectPlanningModule(params.mfhRenovationProjectPlanningModuleId, params.data);
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const createMFHRenovationProjectLotMutation = useMutation({
        mutationFn: async (params: ICreateMFHRenovationProjectLotParams) => {
            await createMfhRenovationProjectLot(params.mfhRenovationProjectId, omit(params, 'mfhRenovationProjectId'));
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const deleteMfhRenovationProjectPlanningModuleMutation = useMutation({
        mutationFn: async (mfhRenovationProjectPlanningModuleId: number) => {
            await deleteMfhRenovationProjectPlanningModule(mfhRenovationProjectPlanningModuleId);
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const updateMFHRenovationProjectLotMutation = useMutation({
        mutationFn: async (params: { mfhRenovationProjectLotId: number; payload: Omit<IUpdateMFHRenovationProjectLotParams, 'mfhRenovationProjectLotId'> }) => {
            await updateMfhRenovationProjectLot(params.mfhRenovationProjectLotId, params.payload);
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const deleteMFHRenovationProjectLotMutation = useMutation({
        mutationFn: async (params: { mfhRenovationProjectLotId: number }) => {
            await deleteMfhRenovationProjectLot(params.mfhRenovationProjectLotId);
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const restoreMfhRenovationProjectPlanningModuleMutation = useMutation({
        mutationFn: async (mfhRenovationProjectPlanningModuleId: number) => {
            await restoreMfhRenovationProjectPlanningModule(mfhRenovationProjectPlanningModuleId);
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const createMFHRenovationProjectPositionsMutation = useMutation({
        mutationFn: async (params: {
            mfhRenovationProjectCostBreakdownId: number;
            mfhRenovationProjectPositions: ICreateMFHRenovationProjectPositions['data'];
            linkableDocumentIds?: number[];
        }) => {
            return createMfhRenovationProjectPositions(
                params.mfhRenovationProjectCostBreakdownId,
                params.mfhRenovationProjectPositions,
                params.linkableDocumentIds,
            );
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const createMFHRenovationProjectPositionMutation = useMutation({
        mutationFn: async (params: { mfhRenovationProjectCostBreakdownId: number; mfhRenovationProjectPosition: IMFHRenovationProjectPosition }) => {
            return createMfhRenovationProjectPosition(params.mfhRenovationProjectCostBreakdownId, params.mfhRenovationProjectPosition);
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const updateMFHRenovationProjectPositionMutation = useMutation({
        mutationFn: async (params: {
            mfhRenovationProjectPositionId: number;
            mfhRenovationProjectPosition: IUpdateMFHRenovationProjectPositionParams['mfhRenovationProjectPosition'];
        }) => {
            return updateMfhRenovationProjectPosition(params.mfhRenovationProjectPositionId, params.mfhRenovationProjectPosition);
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const deleteMfhRenovationProjectPositionMutation = useMutation({
        mutationFn: async (params: { mfhRenovationProjectPositionId: number }) => {
            return deleteMfhRenovationProjectPosition(params.mfhRenovationProjectPositionId);
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const createMFHRenovationProjectCostBreakdownMutation = useMutation({
        mutationFn: async (params: ICreateMFHRenovationProjectCostBreakdownParams) => {
            return createMfhRenovationProjectCostBreakdown(params.mfhRenovationProjectId, omit(params, 'mfhRenovationProjectId'));
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const updateMfhRenovationProjectCostBreakdownMutation = useMutation({
        mutationFn: async (params: { mfhRenovationProjectCostBreakdownId: number; data: IUpdateMFHRenovationProjectCostBreakdownParams['data'] }) => {
            await updateMfhRenovationProjectCostBreakdown(params.mfhRenovationProjectCostBreakdownId, params.data);
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const { mutateAsync: createTenderingProcessMutation } = useMutation(
        async (params: { mfhRenovationProjectId: number; tenderingProcess: CreateTenderingProcessRequest['tenderingProcess'] }) =>
            createTenderingProcess(params.mfhRenovationProjectId, params.tenderingProcess),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['mfh-renovation-projects']);
            },
        },
    );

    const { mutateAsync: updateTenderingProcessMutation } = useMutation(
        async (params: { tenderingProcessId: number; tenderingProcess: UpdateTenderingProcessRequest['tenderingProcess'] }) =>
            updateTenderingProcess(params.tenderingProcessId, params.tenderingProcess),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['mfh-renovation-projects']);
            },
        },
    );

    const { mutateAsync: deleteTenderingProcessMutation } = useMutation(
        async (params: { tenderingProcessId: number }) => deleteTenderingProcess(params.tenderingProcessId),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['mfh-renovation-projects']);
            },
        },
    );

    const triggerMfhRenovationProjectLotEventMutation = useMutation({
        mutationFn: async (params: ITriggerMfhRenovationProjectLotParams) => {
            await postMfhRenovationProjectLotEvent({
                mfhRenovationProjectLotId: params.mfhRenovationProjectLotId,
                event: params.event,
                eventDate: params.eventDate,
                additionalData: params.additionalData,
            });
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const triggerMfhRenovationProjectCostBreakdownEventMutation = useMutation({
        mutationFn: async (params: ITriggerMfhRenovationProjectCostBreakdownParams) => {
            await postProcessMFHRenovationProjectCostBreakdownEvent({
                event: params.event,
                mfhRenovationProjectCostBreakdownId: params.mfhRenovationProjectCostBreakdownId,
                eventDate: params.eventDate,
                additionalData: params.additionalData,
            });
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const triggerMfhRenovationProjectEventMutation = useMutation({
        mutationFn: async (params: ITriggerMfhRenovationProjectParams) => {
            await postMfhRenovationProjectEvent({
                mfhRenovationProjectId: params.mfhRenovationProjectId,
                event: params.event,
                eventDate: params.eventDate,
                additionalData: params.additionalData,
            });
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const triggerMfhRenovationProjectPlanningModuleEventMutation = useMutation({
        mutationFn: async (params: ITriggerMfhRenovationProjectPlanningModuleParams) => {
            await postMfhRenovationProjectPlanningModuleEvent({
                mfhRenovationProjectPlanningModuleId: params.mfhRenovationProjectPlanningModuleId,
                event: params.event,
                eventDate: params.eventDate,
                additionalData: params.additionalData,
            });
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const { mutateAsync: importTenderingProcessOffersMutation } = useMutation({
        mutationFn: async (params: { tenderingProcessId: number; tenderingProcessTendererId: number; file: File }) => {
            return importTenderingProcessOffersFromExcel(params.tenderingProcessId, params.tenderingProcessTendererId, params.file);
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const { mutateAsync: updateTenderingProcessOfferMutation } = useMutation({
        mutationFn: async (params: { tenderingProcessOfferId: number; internalAssessment?: string; isFinal?: boolean }) => {
            return updateTenderingProcessOffer(params.tenderingProcessOfferId, params.internalAssessment, params.isFinal);
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const { mutateAsync: deleteTenderingProcessOfferMutation } = useMutation({
        mutationFn: async (tenderingProcessOfferId: number) => {
            return deleteTenderingProcessOffer(tenderingProcessOfferId);
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const { mutateAsync: updateTenderingProcessWinnerTendererMutation } = useMutation({
        mutationFn: async (params: { tenderingProcessId: number; tenderingProcessTendererId: number; isWinner: boolean }) => {
            return updateTenderingProcessWinnerTenderer(params.tenderingProcessId, params.tenderingProcessTendererId, params.isWinner);
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    const triggerTenderingProcessTendererEventMutation = useMutation({
        mutationFn: async (params: { tenderingProcessTendererId: number; event: TenderingProcessTendererEvents }) => {
            return postTenderingProcessTendererEvent(params);
        },
        onSuccess() {
            queryClient.invalidateQueries(['mfh-renovation-projects']);
        },
    });

    return {
        createMFHRenovationProject: createMfhRenovationProjectMutation,
        updateMFHRenovationProject: updateMfhRenovationProjectMutation,
        createMFHRenovationProjectPositions: createMFHRenovationProjectPositionsMutation,
        createMFHRenovationProjectPosition: createMFHRenovationProjectPositionMutation,
        updateMFHRenovationProjectPosition: updateMFHRenovationProjectPositionMutation,
        createMFHRenovationProjectCostBreakdown: createMFHRenovationProjectCostBreakdownMutation,
        updateMfhRenovationProjectCostBreakdown: updateMfhRenovationProjectCostBreakdownMutation,
        deleteMfhRenovationProject: deleteMfhRenovationProjectMutation,
        deleteMfhRenovationProjectPosition: deleteMfhRenovationProjectPositionMutation,
        updateMfhRenovationProjectPlanningModule: updateMfhRenovationProjectPlanningModuleMutation,
        createMFHRenovationProjectPlanningModule: createMFHRenovationProjectPlanningModuleMutation,
        createMFHRenovationProjectLot: createMFHRenovationProjectLotMutation,
        updateMFHRenovationProjectLot: updateMFHRenovationProjectLotMutation,
        deleteMFHRenovationProjectLot: deleteMFHRenovationProjectLotMutation,
        deleteMfhRenovationProjectPlanningModule: deleteMfhRenovationProjectPlanningModuleMutation,
        restoreMfhRenovationProject: restoreMfhRenovationProjectMutation,
        restoreMfhRenovationProjectPlanningModule: restoreMfhRenovationProjectPlanningModuleMutation,
        createTenderingProcess: createTenderingProcessMutation,
        updateTenderingProcess: updateTenderingProcessMutation,
        deleteTenderingProcess: deleteTenderingProcessMutation,
        triggerMfhRenovationProjectLotEvent: triggerMfhRenovationProjectLotEventMutation,
        triggerMfhRenovationProjectCostBreakdownEvent: triggerMfhRenovationProjectCostBreakdownEventMutation,
        triggerMfhRenovationProjectEvent: triggerMfhRenovationProjectEventMutation,
        triggerMfhRenovationProjectPlanningModuleEvent: triggerMfhRenovationProjectPlanningModuleEventMutation,
        importTenderingProcessOffers: importTenderingProcessOffersMutation,
        updateTenderingProcessOffer: updateTenderingProcessOfferMutation,
        deleteTenderingProcessOffer: deleteTenderingProcessOfferMutation,
        updateTenderingProcessWinnerTenderer: updateTenderingProcessWinnerTendererMutation,
        triggerTenderingProcessTendererEvent: triggerTenderingProcessTendererEventMutation,
    };
};

export const useUploadOfferModal = () => {
    const UploadOfferModal = defineComponent({
        name: 'UploadOfferModal',
        props: {
            tenderer: {
                type: Object as () => ITenderingProcessTenderer,
                required: true,
            },
            tenderingProcessId: {
                type: Number,
                required: true,
            },
        },
        emits: ['complete', 'cancel'],
        setup(props, { emit }) {
            const handleUploadComplete = (result: IImportTenderingProcessOffersResult) => {
                emit('complete', result);
            };

            const handleClose = () => {
                emit('cancel');
            };

            return () =>
                h('div', { class: 'w-full rounded-lg bg-white p-6' }, [
                    h('div', { class: 'mb-4' }, [
                        h('p', { class: 'text-sm text-gray-600' }, [
                            'Upload an Excel file containing the offer details for ',
                            h('strong', props.tenderer.company?.name || 'Unknown Company'),
                        ]),
                    ]),
                    h(ExcelFileUpload, {
                        tenderingProcessId: props.tenderingProcessId,
                        tenderingProcessTendererId: props.tenderer.tenderingProcessTendererId,
                        onUploadComplete: handleUploadComplete,
                    }),
                    h('div', { class: 'mt-4 flex justify-end' }, [h(NButton, { onClick: handleClose, size: 'small' }, 'Close')]),
                ]);
        },
    });

    const openUploadOfferModal = (params: {
        tenderer: ITenderingProcessTenderer;
        tenderingProcessId: number;
    }): Promise<{ result: IImportTenderingProcessOffersResult; close: () => void } | null> => {
        return new Promise(resolve => {
            const modalInstance = showModal(UploadOfferModal, {
                width: 600,
                props: {
                    tenderer: params.tenderer,
                    tenderingProcessId: params.tenderingProcessId,
                },
                events: {
                    complete: (result: IImportTenderingProcessOffersResult) => {
                        resolve({
                            result,
                            close: () => modalInstance.close(),
                        });
                    },
                    cancel: () => {
                        modalInstance.close();
                        resolve(null);
                    },
                },
            });
        });
    };

    return { openUploadOfferModal };
};
